import React from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  AlertCircle,
  CircleCheckBig,
  Download,
  IndianRupee,
  TrendingUp,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import { hasPermissionAction } from "../../../Utils/organizationPermissions";
import { MAIN_URL } from "../../../Configurations/Urls";

const MONTHS = [
  { value: 1, label: "Jan" },
  { value: 2, label: "Feb" },
  { value: 3, label: "Mar" },
  { value: 4, label: "Apr" },
  { value: 5, label: "May" },
  { value: 6, label: "Jun" },
  { value: 7, label: "Jul" },
  { value: 8, label: "Aug" },
  { value: 9, label: "Sep" },
  { value: 10, label: "Oct" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dec" },
];

function n(val) {
  const x = Number(val);
  return Number.isFinite(x) ? x : 0;
}

function fmtNumber(val) {
  return n(val).toLocaleString("en-IN");
}

function fmtMoney(val) {
  return n(val).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function fmtInr(val) {
  const x = n(val);
  const formatted = Number.isInteger(x)
    ? x.toLocaleString("en-IN")
    : x.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
  return `₹${formatted}`;
}

function toMonthYYYYMM(y, m) {
  return `${String(y)}-${String(m).padStart(2, "0")}`;
}

function buildYearOptions(centerYear) {
  const y = Number(centerYear);
  const base = Number.isFinite(y) ? y : new Date().getFullYear();
  const start = Math.max(2000, base - 5);
  const end = Math.min(2100, base + 5);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

function buildYearOptionsForRange(y1, y2) {
  const a = Number(y1);
  const b = Number(y2);
  const lo = Math.min(
    Number.isFinite(a) ? a : new Date().getFullYear(),
    Number.isFinite(b) ? b : new Date().getFullYear()
  );
  const hi = Math.max(
    Number.isFinite(a) ? a : new Date().getFullYear(),
    Number.isFinite(b) ? b : new Date().getFullYear()
  );
  const start = Math.max(2000, lo - 5);
  const end = Math.min(2100, hi + 5);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

function toMonthIndex(y, m) {
  return Number(y) * 12 + (Number(m) - 1);
}

function enumerateMonthRange(fromYear, fromMonth, toYear, toMonth) {
  let start = toMonthIndex(fromYear, fromMonth);
  let end = toMonthIndex(toYear, toMonth);
  if (start > end) {
    const tmp = start;
    start = end;
    end = tmp;
  }
  const months = [];
  for (let i = start; i <= end; i += 1) {
    const y = Math.floor(i / 12);
    const m = (i % 12) + 1;
    months.push({
      year: y,
      month: m,
      label: MONTHS[m - 1]?.label || String(m),
    });
  }
  return months;
}

function formatPeriodLabel(fromYear, fromMonth, toYear, toMonth) {
  const fromLbl = `${MONTHS[fromMonth - 1]?.label || fromMonth} ${fromYear}`;
  const toLbl = `${MONTHS[toMonth - 1]?.label || toMonth} ${toYear}`;
  if (fromYear === toYear && fromMonth === toMonth) return fromLbl;
  return `${fromLbl} – ${toLbl}`;
}

function NumCell({ value, kind = "count" }) {
  const v = n(value);
  if (!v) return <span className="ems-rep-zero">0</span>;
  const cls =
    kind === "paid" ? "ems-rep-paid" : kind === "due" ? "ems-rep-due" : "ems-rep-count";
  return <span className={cls}>{fmtNumber(v)}</span>;
}

function InrCell({ value, kind = "total" }) {
  const v = n(value);
  if (!v) return <span className="ems-rep-zero">{fmtInr(0)}</span>;
  const cls =
    kind === "paid" ? "ems-rep-paid" : kind === "due" ? "ems-rep-due" : "ems-rep-total";
  return <span className={`${cls} ems-tnum`}>{fmtInr(v)}</span>;
}

function RepNavButton({ className, onClick, children }) {
  return (
    <button type="button" className={`ems-rep-nav ${className || ""}`} onClick={onClick}>
      {children}
    </button>
  );
}

export default function FeeDueReportMonthly() {
  const navigate = useNavigate();
  const { year: yearParam, month: monthParam } = useParams();
  const { userData } = useAuthStore();
  const { Permission } = usePermissionDataStore();
  const canDownloadFeeDueReport = React.useMemo(
    () => hasPermissionAction(Permission, "DOWNLOAD_FEE_DUE_REPORT"),
    [Permission]
  );
  const organizationId = userData?.organization?.organization_id;
  const organizationName =
    userData?.organization?.organization_name ||
    userData?.organization?.organization_short_name ||
    userData?.activeOrganizationEntity?.organization_name ||
    userData?.activeOrganizationEntity?.organization_short_name ||
    "";

  const now = React.useMemo(() => new Date(), []);
  const paramYear = Number(yearParam);
  const paramMonth = Number(monthParam);
  const paramOk =
    Number.isInteger(paramYear) &&
    paramYear >= 2000 &&
    paramYear <= 2100 &&
    Number.isInteger(paramMonth) &&
    paramMonth >= 1 &&
    paramMonth <= 12;

  React.useEffect(() => {
    if (paramOk) navigate("/fee-due-report", { replace: true });
  }, [paramOk, navigate]);

  const [year, setYear] = React.useState(() =>
    paramOk ? paramYear : now.getFullYear()
  );
  const [month, setMonth] = React.useState(() =>
    paramOk ? paramMonth : now.getMonth() + 1
  );
  const [fromYear, setFromYear] = React.useState(() =>
    paramOk ? paramYear : 2026
  );
  const [fromMonth, setFromMonth] = React.useState(() =>
    paramOk ? paramMonth : 4
  );
  const [toYear, setToYear] = React.useState(() =>
    paramOk ? paramYear : 2027
  );
  const [toMonth, setToMonth] = React.useState(() =>
    paramOk ? paramMonth : 3
  );
  const [loading, setLoading] = React.useState(false);
  const [errorText, setErrorText] = React.useState("");
  const [report, setReport] = React.useState(null);
  const [yearRowsLoading, setYearRowsLoading] = React.useState(false);
  const [yearRows, setYearRows] = React.useState([]);

  const periodLabel = React.useMemo(
    () => formatPeriodLabel(fromYear, fromMonth, toYear, toMonth),
    [fromYear, fromMonth, toYear, toMonth]
  );

  const yearOptions = React.useMemo(
    () => buildYearOptionsForRange(fromYear, toYear),
    [fromYear, toYear]
  );

  const applyFromMonth = (m) => {
    const nextM = Number(m);
    setFromMonth(nextM);
    if (toMonthIndex(fromYear, nextM) > toMonthIndex(toYear, toMonth)) {
      setToYear(fromYear);
      setToMonth(nextM);
    }
  };

  const applyFromYear = (y) => {
    const nextY = Number(y);
    setFromYear(nextY);
    if (toMonthIndex(nextY, fromMonth) > toMonthIndex(toYear, toMonth)) {
      setToYear(nextY);
      setToMonth(fromMonth);
    }
  };

  const applyToMonth = (m) => {
    const nextM = Number(m);
    setToMonth(nextM);
    if (toMonthIndex(fromYear, fromMonth) > toMonthIndex(toYear, nextM)) {
      setFromYear(toYear);
      setFromMonth(nextM);
    }
  };

  const applyToYear = (y) => {
    const nextY = Number(y);
    setToYear(nextY);
    if (toMonthIndex(fromYear, fromMonth) > toMonthIndex(nextY, toMonth)) {
      setFromYear(nextY);
      setFromMonth(toMonth);
    }
  };

  const yearTotals = React.useMemo(() => {
    return (yearRows || []).reduce(
      (acc, row) => {
        const d = row?.data || {};
        acc.totalCount += n(d?.fee_total_count_this_month);
        acc.totalInr += n(d?.fee_total_amount_inr_this_month);
        acc.dueCount += n(d?.fee_due?.count);
        acc.dueInr += n(d?.fee_due?.amount_inr);
        acc.paidCount += n(d?.fee_paid?.count);
        acc.paidInr += n(d?.fee_paid?.amount_inr);
        return acc;
      },
      {
        totalCount: 0,
        totalInr: 0,
        dueCount: 0,
        dueInr: 0,
        paidCount: 0,
        paidInr: 0,
      }
    );
  }, [yearRows]);

  const collectionRate = React.useMemo(() => {
    if (yearTotals.totalInr <= 0) return 0;
    return Math.round((yearTotals.paidInr / yearTotals.totalInr) * 100);
  }, [yearTotals]);

  const maxInr = React.useMemo(() => {
    const vals = (yearRows || []).map((row) =>
      n(row?.data?.fee_total_amount_inr_this_month)
    );
    return Math.max(1, ...vals, 0);
  }, [yearRows]);

  const downloadYearPdf = React.useCallback(async () => {
    if (!canDownloadFeeDueReport) {
      toast.error("You do not have permission to download the fee due report.");
      return;
    }
    try {
      const mod = await import("jspdf");
      const jsPDF = mod.jsPDF || mod.default?.jsPDF || mod.default;
      const doc = new jsPDF({ unit: "pt", format: "a4" });

      const marginX = 40;
      let yPos = 48;
      const lineH = 16;

      doc.setFontSize(14);
      doc.text(
        `Monthly Fee Due Report (${periodLabel})${organizationName ? ` — ${organizationName}` : ""}`,
        marginX,
        yPos
      );
      yPos += 18;

      doc.setFontSize(10);
      doc.setTextColor(120);
      doc.text("Monthly summary: Total / Paid / Due", marginX, yPos);
      doc.setTextColor(0);
      yPos += 20;

      const cols = {
        month: marginX,
        totalCount: 160,
        totalInr: 230,
        paidCount: 330,
        paidInr: 390,
        dueCount: 480,
        dueInr: 540,
      };

      doc.setFontSize(9);
      doc.setFont(undefined, "bold");
      doc.text("Month", cols.month, yPos);
      doc.text("Total#", cols.totalCount, yPos, { align: "right" });
      doc.text("Total INR", cols.totalInr, yPos, { align: "right" });
      doc.text("Paid#", cols.paidCount, yPos, { align: "right" });
      doc.text("Paid INR", cols.paidInr, yPos, { align: "right" });
      doc.text("Due#", cols.dueCount, yPos, { align: "right" });
      doc.text("Due INR", cols.dueInr, yPos, { align: "right" });
      doc.setFont(undefined, "normal");

      yPos += 10;
      doc.setDrawColor(180);
      doc.line(marginX, yPos, 555, yPos);
      yPos += 14;

      const rows = (yearRows || []).map((row) => {
        const d = row?.data || {};
        return {
          month: `${row?.label || "-"} ${row?.year ?? ""}`,
          totalCount: fmtNumber(d?.fee_total_count_this_month),
          totalInr: fmtMoney(d?.fee_total_amount_inr_this_month),
          paidCount: fmtNumber(d?.fee_paid?.count),
          paidInr: fmtMoney(d?.fee_paid?.amount_inr),
          dueCount: fmtNumber(d?.fee_due?.count),
          dueInr: fmtMoney(d?.fee_due?.amount_inr),
        };
      });

      const writeRow = (r, isTotal = false) => {
        if (yPos > 780) {
          doc.addPage();
          yPos = 48;
        }
        if (isTotal) doc.setFont(undefined, "bold");
        doc.text(String(r.month), cols.month, yPos);
        doc.text(String(r.totalCount), cols.totalCount, yPos, { align: "right" });
        doc.text(String(r.totalInr), cols.totalInr, yPos, { align: "right" });
        doc.text(String(r.paidCount), cols.paidCount, yPos, { align: "right" });
        doc.text(String(r.paidInr), cols.paidInr, yPos, { align: "right" });
        doc.text(String(r.dueCount), cols.dueCount, yPos, { align: "right" });
        doc.text(String(r.dueInr), cols.dueInr, yPos, { align: "right" });
        if (isTotal) doc.setFont(undefined, "normal");
        yPos += lineH;
      };

      rows.forEach((r) => writeRow(r, false));

      yPos += 4;
      doc.setDrawColor(180);
      doc.line(marginX, yPos, 555, yPos);
      yPos += 14;

      writeRow(
        {
          month: "Total",
          totalCount: fmtNumber(yearTotals.totalCount),
          totalInr: fmtMoney(yearTotals.totalInr),
          paidCount: fmtNumber(yearTotals.paidCount),
          paidInr: fmtMoney(yearTotals.paidInr),
          dueCount: fmtNumber(yearTotals.dueCount),
          dueInr: fmtMoney(yearTotals.dueInr),
        },
        true
      );

      const fileSuffix = periodLabel.replace(/\s+/g, "_").replace(/–/g, "-");
      doc.save(`Fee_Due_Report_${fileSuffix}.pdf`);
    } catch (e) {
      toast.error("Could not generate PDF.");
      console.error("FeeDueReportMonthly PDF error:", e);
    }
  }, [canDownloadFeeDueReport, periodLabel, yearRows, yearTotals, organizationName]);

  const fetchReport = React.useCallback(
    async (y, m) => {
      if (!organizationId) return;
      setLoading(true);
      setErrorText("");
      try {
        const token = localStorage.getItem("token");
        const endpoint = `${MAIN_URL}/api/ems/fee-due-report-monthly`;
        const res = await axios.post(
          endpoint,
          { organization_id: organizationId, year: y, month: m },
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        setReport(res?.data?.data || null);
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to fetch report";
        setErrorText(msg);
        setReport(null);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    },
    [organizationId]
  );

  React.useEffect(() => {
    if (!organizationId) return;
    if (paramOk) fetchReport(year, month);
  }, [organizationId, year, month, fetchReport, paramOk]);

  const period = report?.period || {};
  const feeDue = report?.fee_due || {};
  const feePaid = report?.fee_paid || {};

  React.useEffect(() => {
    if (!organizationId) return;
    if (paramOk) return;
    if (!fromYear || !toYear) return;
    if (!Number.isFinite(Number(fromYear)) || !Number.isFinite(Number(toYear))) return;
    if (!fromMonth || !toMonth) return;

    let isActive = true;
    const loadYearRows = async () => {
      setYearRowsLoading(true);
      setErrorText("");
      try {
        const token = localStorage.getItem("token");
        const endpoint = `${MAIN_URL}/api/ems/fee-due-report-monthly`;
        const range = enumerateMonthRange(fromYear, fromMonth, toYear, toMonth);
        const requests = range.map((item) =>
          axios
            .post(
              endpoint,
              {
                organization_id: organizationId,
                year: Number(item.year),
                month: Number(item.month),
              },
              {
                headers: {
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
              }
            )
            .then((res) => ({
              year: item.year,
              month: item.month,
              label: item.label,
              data: res?.data?.data || null,
            }))
            .catch(() => ({
              year: item.year,
              month: item.month,
              label: item.label,
              data: null,
            }))
        );
        const rows = await Promise.all(requests);
        if (!isActive) return;
        setYearRows(rows);
      } catch (err) {
        if (!isActive) return;
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to fetch year report";
        setErrorText(msg);
        setYearRows([]);
      } finally {
        if (!isActive) return;
        setYearRowsLoading(false);
      }
    };

    loadYearRows();
    return () => {
      isActive = false;
    };
  }, [organizationId, fromYear, fromMonth, toYear, toMonth, paramOk]);

  if (!paramOk) {
    return (
      <div className="ems-dashboard ems-fade-up">
        <div className="ems-page-head">
          <div>
            <h1 className="ems-page-title">Monthly Fee Due Report</h1>
          </div>
          <div className="ems-head-actions">
            <div className="ems-range-pick">
              <div className="ems-range-pick-group">
                <span className="ems-yp-lbl">From</span>
                <select
                  className="ems-select ems-select-month"
                  value={fromMonth}
                  onChange={(e) => applyFromMonth(e.target.value)}
                  aria-label="From month"
                >
                  {MONTHS.map((m) => (
                    <option key={`from-m-${m.value}`} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <select
                  className="ems-select ems-select-year"
                  value={fromYear}
                  onChange={(e) => applyFromYear(e.target.value)}
                  aria-label="From year"
                >
                  {yearOptions.map((y) => (
                    <option key={`from-y-${y}`} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ems-range-pick-sep" aria-hidden="true" />
              <div className="ems-range-pick-group">
                <span className="ems-yp-lbl">To</span>
                <select
                  className="ems-select ems-select-month"
                  value={toMonth}
                  onChange={(e) => applyToMonth(e.target.value)}
                  aria-label="To month"
                >
                  {MONTHS.map((m) => (
                    <option key={`to-m-${m.value}`} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <select
                  className="ems-select ems-select-year"
                  value={toYear}
                  onChange={(e) => applyToYear(e.target.value)}
                  aria-label="To year"
                >
                  {yearOptions.map((y) => (
                    <option key={`to-y-${y}`} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {canDownloadFeeDueReport ? (
              <button
                type="button"
                className="ems-btn ems-primary"
                onClick={downloadYearPdf}
                disabled={yearRowsLoading || !yearRows?.length}
              >
                <Download size={15} />
                Download PDF
              </button>
            ) : null}
          </div>
        </div>

        <div className="ems-section-divider" />

        {errorText ? (
          <div style={{ color: "var(--ems-red)", fontSize: 13, marginBottom: 12 }}>
            {errorText}
          </div>
        ) : null}

        <div className="ems-grid ems-g-4 ems-kpi-strip">
          <div className="ems-card ems-kpi">
            <div className="ems-kpi-top">
              <div className="ems-kpi-ic">
                <IndianRupee size={20} />
              </div>
            </div>
            <div className="ems-kpi-val lg ems-tnum">{fmtInr(yearTotals.totalInr)}</div>
            <div className="ems-kpi-lbl">Total Billed · {periodLabel}</div>
          </div>
          <div className="ems-card ems-kpi">
            <div className="ems-kpi-top">
              <div className="ems-kpi-ic ok">
                <CircleCheckBig size={19} />
              </div>
            </div>
            <div className="ems-kpi-val lg ok ems-tnum">{fmtInr(yearTotals.paidInr)}</div>
            <div className="ems-kpi-lbl">
              Collected · {fmtNumber(yearTotals.paidCount)} payments
            </div>
          </div>
          <div className="ems-card ems-kpi">
            <div className="ems-kpi-top">
              <div className="ems-kpi-ic bad">
                <AlertCircle size={19} />
              </div>
            </div>
            <div className="ems-kpi-val lg bad ems-tnum">{fmtInr(yearTotals.dueInr)}</div>
            <div className="ems-kpi-lbl">
              Outstanding · {fmtNumber(yearTotals.dueCount)} due
            </div>
          </div>
          <div className="ems-card ems-kpi">
            <div className="ems-kpi-top">
              <div className="ems-kpi-ic teal">
                <TrendingUp size={19} />
              </div>
            </div>
            <div className="ems-kpi-val ems-tnum">{collectionRate}%</div>
            <div className="ems-kpi-lbl">Collection Rate</div>
            <div className="ems-kpi-bar">
              <div style={{ width: `${collectionRate}%` }} />
            </div>
          </div>
        </div>

        <div className="ems-card ems-table-card">
          {yearRowsLoading ? (
            <div className="ems-loading-screen">
              <CircularProgress size={28} />
            </div>
          ) : (
            <>
              <div className="ems-rep-legend">
                <span className="ems-rep-leg-item">
                  <span
                    className="ems-rep-leg-sw"
                    style={{ background: "var(--ems-green)" }}
                  />
                  Collected
                </span>
                <span className="ems-rep-leg-item">
                  <span
                    className="ems-rep-leg-sw"
                    style={{ background: "var(--ems-red)" }}
                  />
                  Outstanding
                </span>
              </div>
              <div className="ems-table-wrap">
                <table className="ems-data ems-fee-due-table">
                  <thead>
                    <tr>
                      <th style={{ width: 120 }}>Month</th>
                      <th className="ems-num">Total Count</th>
                      <th className="ems-num">Total INR</th>
                      <th className="ems-num">Paid Count</th>
                      <th className="ems-num">Paid INR</th>
                      <th className="ems-num">Due Count</th>
                      <th className="ems-num">Due INR</th>
                      <th style={{ width: 170 }}>Collection</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yearRows.map((row) => {
                      const d = row?.data || {};
                      const due = d?.fee_due || {};
                      const paid = d?.fee_paid || {};
                      const totalCount = n(d?.fee_total_count_this_month);
                      const totalInr = n(d?.fee_total_amount_inr_this_month);
                      const paidCount = n(paid?.count);
                      const paidInr = n(paid?.amount_inr);
                      const dueCount = n(due?.count);
                      const dueInr = n(due?.amount_inr);
                      const pct = totalInr
                        ? Math.round((paidInr / totalInr) * 100)
                        : 0;
                      const rowYear = row.year ?? fromYear;
                      const monthKey = toMonthYYYYMM(rowYear, row.month);

                      return (
                        <tr
                          key={`m-${rowYear}-${row.month}`}
                          className={totalCount === 0 ? "ems-rep-empty-row" : ""}
                        >
                          <td>
                            <span className="ems-rep-month">
                              {row.label} {rowYear}
                            </span>
                          </td>
                          <td className="ems-num">
                            {totalCount > 0 ? (
                              <RepNavButton
                                onClick={() =>
                                  navigate(
                                    `/fee-installments?month=${encodeURIComponent(monthKey)}`
                                  )
                                }
                              >
                                <NumCell value={totalCount} kind="count" />
                              </RepNavButton>
                            ) : (
                              <NumCell value={totalCount} kind="count" />
                            )}
                          </td>
                          <td className="ems-num">
                            <InrCell value={totalInr} kind="total" />
                          </td>
                          <td className="ems-num">
                            {paidCount > 0 ? (
                              <RepNavButton
                                onClick={() =>
                                  navigate(
                                    `/fee-installments?month=${encodeURIComponent(monthKey)}&status=paid`
                                  )
                                }
                              >
                                <NumCell value={paidCount} kind="paid" />
                              </RepNavButton>
                            ) : (
                              <NumCell value={paidCount} kind="paid" />
                            )}
                          </td>
                          <td className="ems-num">
                            <InrCell value={paidInr} kind="paid" />
                          </td>
                          <td className="ems-num">
                            {dueCount > 0 ? (
                              <RepNavButton
                                onClick={() =>
                                  navigate(
                                    `/fee-installments?month=${encodeURIComponent(monthKey)}&status=pending`
                                  )
                                }
                              >
                                <NumCell value={dueCount} kind="due" />
                              </RepNavButton>
                            ) : (
                              <NumCell value={dueCount} kind="due" />
                            )}
                          </td>
                          <td className="ems-num">
                            <InrCell value={dueInr} kind="due" />
                          </td>
                          <td>
                            {totalInr > 0 ? (
                              <div className="ems-rep-bar" title={`${pct}% collected`}>
                                <div
                                  className="ems-rep-bar-track"
                                  style={{ width: `${(totalInr / maxInr) * 100}%` }}
                                >
                                  <div
                                    className="ems-rep-bar-paid"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                            ) : (
                              <span className="ems-rep-zero">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="ems-rep-total-row">
                      <td>Total</td>
                      <td className="ems-num">
                        <b>{fmtNumber(yearTotals.totalCount)}</b>
                      </td>
                      <td className="ems-num">
                        <b className="ems-tnum">{fmtInr(yearTotals.totalInr)}</b>
                      </td>
                      <td className="ems-num">
                        <b className="ems-rep-paid">{fmtNumber(yearTotals.paidCount)}</b>
                      </td>
                      <td className="ems-num">
                        <b className="ems-rep-paid ems-tnum">
                          {fmtInr(yearTotals.paidInr)}
                        </b>
                      </td>
                      <td className="ems-num">
                        <b className="ems-rep-due">{fmtNumber(yearTotals.dueCount)}</b>
                      </td>
                      <td className="ems-num">
                        <b className="ems-rep-due ems-tnum">
                          {fmtInr(yearTotals.dueInr)}
                        </b>
                      </td>
                      <td>
                        <span className="ems-rep-rate-pill">
                          {collectionRate}% collected
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Monthly Fee Due Report
          </Typography>
          {paramOk && period?.start_date && period?.end_date && (
            <Typography variant="body2" color="text.secondary">
              {period.start_date} → {period.end_date}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <TextField
            size="small"
            select
            label="Year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            sx={{ width: 140 }}
          >
            {buildYearOptions(year).map((y) => (
              <MenuItem key={String(y)} value={y}>
                {y}
              </MenuItem>
            ))}
          </TextField>
          {paramOk ? (
            <>
              <TextField
                size="small"
                select
                label="Month"
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                sx={{ width: 140 }}
              >
                {MONTHS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(`/fee-due-report/${year}/${month}`)}
              >
                Share link
              </Button>
            </>
          ) : null}
        </Box>
      </Box>

      {errorText ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {errorText}
        </Typography>
      ) : null}

      {paramOk && loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : paramOk ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Fee Due (Pending)
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Count
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>
                      {fmtNumber(feeDue?.count)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Amount (INR)
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {fmtMoney(feeDue?.amount_inr)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Fee Paid (Successful / Settled)
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Count
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>
                      {fmtNumber(feePaid?.count)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Amount (INR)
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {fmtMoney(feePaid?.amount_inr)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : null}
    </Box>
  );
}
